import { useAuthContext } from "AuthContextWrapper"
import { documentsAPI } from "apis";
import { UserDocument } from "models/UserDocument";
import React, { createContext, useEffect, useState } from "react";
import { getDocumentContents } from "views/fileCabinet/getDocumentContents";

export const documentTypeIdsToNames = [
  "", // don't use this, there is no document type with id 0
  "Other",
  "Estate",
  "Death certificate",
  "Will",
  "Bill / Invoice",
  "Financial Statement",
  "Legal Document",
  "Correspondances",
  "Photo Verification",
  "Driver’s License",
  "Passport",
  "Military I.D.",
  "Tribal Card",
  "Letters Testamentary",
];

export const DOC_TYPE_ID_TRIBAL_CARD = 13
export const DOC_TYPE_ID_PHOTO_VERIFICATION = 9
export const DOC_TYPE_ID_PASSPORT = 11
export const DOC_TYPE_ID_DRIVERS_LICENSE = 10
export const DOC_TYPE_ID_MILITARY_ID = 12
export const DOC_TYPE_ID_WILL = 4
export const DOC_TYPE_ID_LETTERS_TESTAMENTARY = 14
export const DOC_TYPE_ID_DEATH_CERTIFICATE = 3

interface Props {
  children: JSX.Element;
}

export interface FileCabinetContextInterface {
  docs: UserDocument[],
  refreshDocs: () => void,
  setDocs: (docs: UserDocument[]) => any,
  docSrcs: string[],
  setDocSrcs: (srcs: string[]) => any,
  loadingDocs: boolean,
  loadingSrcs: boolean,
}

export const FileCabinetContext = createContext<FileCabinetContextInterface>({
  docs: [],
  docSrcs: [],
  loadingDocs: true,
  loadingSrcs: true,
  refreshDocs: () => undefined,
  setDocs: () => undefined,
  setDocSrcs: () => undefined,
});

export const FileCabinetContextWrapper = ({ children }: Props): JSX.Element => {
  const { activeEstate } = useAuthContext()
  const [docs, setDocs] = useState<UserDocument[]>([]);
  const [docSrcs, setDocSrcs] = useState<string[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [loadingSrcs, setLoadingSrcs] = useState(true);

  useEffect(() => {
    loadAll();
  }, [activeEstate]);

  const refreshDocs = async () => {
    loadAll(true);
  };

  const loadAll = async (skipLoading = false) => {
    if (!skipLoading) {
      setLoadingDocs(true);
      setLoadingSrcs(true);
    }
    documentsAPI
      .getDocuments()
      .then(async (myDocs: UserDocument[] | undefined) => {
        // Sort by doc creation date desc.  This will make it so newer docs
        // will consistently show up at the top
        myDocs = myDocs?.sort((d1, d2) => {
          if (!d2 || !d2.updatedAt) {
            console.warn("missing updated_at in document")
            return -1;
          }
          if (!d1 || !d1.updatedAt) {
            console.warn("missing updated_at in document 2")
            return 1;
          }
          return d1.updatedAt > d2.updatedAt ? 1 : -1;
        }) || [];

        setDocs(myDocs);
        setLoadingDocs(false);

        // We can fetch async but make sure to keep srcs in order with docs
        // Note that document contents are cached by the getDocumentContents function
        const contentPromises: Promise<string | undefined>[] = [];
        for (let i = 0; i < (myDocs?.length || 0); i++) {
          if (!myDocs) {
            break;
          }
          const doc = myDocs[i];
          if (!doc.id || !doc.version || !doc.version.s3Key) {
            continue;
          }
          contentPromises.push(
            getDocumentContents(
              doc.id,
              doc.version.id,
              undefined,
              !!doc.version.thumbnailS3Key,
              Math.floor(Math.random() * 1000),
            )
          );
        }

        const docContents: (string | undefined)[] = await Promise.all(contentPromises)
        const newDocContents: string[] = []
        docContents.map(dc => {
          if (dc) {
            newDocContents.push(dc)
          }
        })

        setDocSrcs(newDocContents);
        if (!skipLoading) {
          setLoadingSrcs(false);
        }
      });
  };

  return (
    <FileCabinetContext.Provider
      value={{
        docs,
        docSrcs,
        setDocSrcs,
        loadingDocs,
        loadingSrcs,
        refreshDocs,
        setDocs,
      }}
    >
      {children}
    </FileCabinetContext.Provider>
  );
};
